import { runAfterIdle } from './idle';

const INTERCOM_APP_ID = 'x6oeppkc';
const INTERCOM_SCRIPT_URL = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;
const INTERCOM_SCRIPT_ID = 'ora-intercom-widget';

export const ANONYMOUS_INTERCOM_SETTINGS: IntercomSettings = {
  api_base: 'https://api-iam.intercom.io',
  app_id: INTERCOM_APP_ID,
  source: 'ora',
};

type QueuedIntercom = IntercomAPI & {
  q?: unknown[];
  c?: (args: unknown[]) => void;
};

let intercomScriptPromise: Promise<boolean> | null = null;
let pendingBootSettings: IntercomSettings | null = null;
let isBooted = false;
const isBrowser = () => typeof window !== 'undefined';

const installIntercomQueue = () => {
  if (!isBrowser() || typeof window.Intercom === 'function') {
    return;
  }

  const intercom = ((...args: unknown[]) => {
    intercom.c?.(args);
  }) as QueuedIntercom;

  intercom.q = [];

  intercom.c = (args: unknown[]) => {
    intercom.q?.push(args);
  };

  window.Intercom = intercom;
};

export const loadIntercom = () => {
  if (!isBrowser()) {
    return Promise.resolve(false);
  }

  installIntercomQueue();

  if (document.getElementById(INTERCOM_SCRIPT_ID)) {
    return intercomScriptPromise ?? Promise.resolve(true);
  }

  intercomScriptPromise = new Promise<boolean>((resolve) => {
    const script = document.createElement('script');
    script.id = INTERCOM_SCRIPT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.src = INTERCOM_SCRIPT_URL;
    script.onload = () => resolve(true);

    script.onerror = () => {
      intercomScriptPromise = null;
      script.remove();
      console.warn('Failed to load Intercom');
      resolve(false);
    };

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode?.insertBefore(script, firstScript);
  });

  return intercomScriptPromise;
};

export type IntercomEvent = 'onShow' | 'onHide' | 'onUnreadCountChange';

type IntercomEventCallback = (count: number) => void;

/**
 * Subscribers live here rather than on `window.Intercom` directly, because a
 * `shutdown` and re-boot drops every handler Intercom holds. One dispatcher
 * per event is (re)registered on each boot and fans out to whatever is in this
 * registry at the time, so a subscription taken once at mount survives the
 * logout/login cycle that re-boots the widget.
 */
const eventListeners: Record<IntercomEvent, Set<IntercomEventCallback>> = {
  onShow: new Set(),
  onHide: new Set(),
  onUnreadCountChange: new Set(),
};

const INTERCOM_EVENTS = Object.keys(eventListeners) as IntercomEvent[];

const attachIntercomListeners = () => {
  if (!isBrowser() || typeof window.Intercom !== 'function') return;

  INTERCOM_EVENTS.forEach((event) => {
    const dispatch = (count: number) => {
      eventListeners[event].forEach((listener) => listener(count));
    };

    if (event === 'onUnreadCountChange') {
      window.Intercom?.(event, dispatch);
      return;
    }

    window.Intercom?.(event, () => dispatch(0));
  });
};

/**
 * Listen for a Messenger lifecycle event. Safe to call before the script has
 * loaded: the command queue is installed first and Intercom replays it on
 * boot. Returns an unsubscribe.
 */
export const subscribeIntercom = (
  event: IntercomEvent,
  callback: IntercomEventCallback
) => {
  if (!isBrowser()) return () => {};

  installIntercomQueue();
  eventListeners[event].add(callback);

  // A boot that already happened registered the dispatchers; one that has not
  // will register them itself.
  if (isBooted) attachIntercomListeners();

  return () => {
    eventListeners[event].delete(callback);
  };
};

export const bootIntercom = async (settings: IntercomSettings) => {
  pendingBootSettings = settings;
  const didLoad = await loadIntercom();
  if (!didLoad) return false;

  window.Intercom?.('boot', settings);
  isBooted = true;
  attachIntercomListeners();
  return true;
};

export const scheduleIntercomBoot = (settings: IntercomSettings) => {
  pendingBootSettings = settings;

  return runAfterIdle(() => {
    if (pendingBootSettings !== settings) return;
    void bootIntercom(settings);
  });
};

/**
 * Boots Intercom for the session: as the signed-in user, or anonymously
 * (after clearing any identified session) when nobody is signed in or an
 * admin is impersonating, who must not see or write in the student's
 * conversations (ORA-1089). Returns the cancel of the scheduled boot.
 */
export const scheduleSessionIntercomBoot = ({
  user,
  isImpersonating,
}: {
  user: IntercomSettings | null;
  isImpersonating: boolean;
}): (() => void) => {
  if (user && !isImpersonating) return scheduleIntercomBoot(user);
  shutdownIntercom();
  return scheduleIntercomBoot(ANONYMOUS_INTERCOM_SETTINGS);
};

const ensureIntercomBooted = async () => {
  if (isBooted) return true;
  // Logged-out pages never schedule a user boot, so fall back to an
  // anonymous boot rather than silently dropping the interaction.
  return bootIntercom(pendingBootSettings ?? ANONYMOUS_INTERCOM_SETTINGS);
};

export const shutdownIntercom = () => {
  pendingBootSettings = null;
  isBooted = false;

  if (!isBrowser() || typeof window.Intercom !== 'function') {
    return;
  }

  window.Intercom('shutdown');
};

export const showIntercom = async () => {
  const didBoot = await ensureIntercomBooted();
  if (!didBoot) return false;

  const didLoad = await loadIntercom();
  if (!didLoad) return false;

  window.Intercom?.('show');
  return true;
};

/** Close the Messenger, leaving the conversation where it was. */
export const hideIntercom = () => {
  if (!isBrowser() || typeof window.Intercom !== 'function') return;

  window.Intercom('hide');
};

/**
 * Whether the real widget is on the page, rather than the command queue this
 * module installs for it.
 *
 * The queue stub carries a `q` array and Intercom's own script replaces the
 * global with a function that has no such array. A stub that is still there
 * after a boot means the widget never took over — blocked by an extension or a
 * content policy, a native shell, a headless browser — and every command from
 * here on is quietly pushed into an array nobody reads. `booted` is not part of
 * Intercom's documented surface, so this heuristic is what we can rely on.
 */
const isIntercomWidgetReady = () =>
  isBrowser() &&
  typeof window.Intercom === 'function' &&
  !Array.isArray((window.Intercom as QueuedIntercom).q);

/**
 * Open the Messenger on its conversation list — the "Human →" swap in the
 * companion shell, where the student is switching channel rather than starting
 * a specific new thread.
 *
 * Returns false when the Messenger will not appear, so the shell can come back
 * rather than leave the student looking at a page with nothing on it.
 */
export const showIntercomMessages = async () => {
  const didBoot = await ensureIntercomBooted();
  if (!didBoot) return false;

  const didLoad = await loadIntercom();
  if (!didLoad) return false;
  if (!isIntercomWidgetReady()) return false;

  window.Intercom?.('showMessages');
  return true;
};

/**
 * Open the support composer, optionally prefilled with `message`.
 *
 * The prefill is Intercom's documented second argument to `showNewMessage`: it
 * drops the text into the composer without sending it, so the student still
 * reads and edits what leaves their account. Callers that just want an empty
 * composer keep passing nothing.
 *
 * Returns false when the widget could not be loaded or booted — a native shell
 * where it never arrives, or a blocked script — so the caller can offer the
 * text another way rather than losing it.
 */
export const showNewIntercomMessage = async (message?: string) => {
  const didBoot = await ensureIntercomBooted();
  if (!didBoot) return false;

  const didLoad = await loadIntercom();
  if (!didLoad) return false;

  if (message) {
    window.Intercom?.('showNewMessage', message);
    return true;
  }

  window.Intercom?.('showNewMessage');
  return true;
};
